import { useState } from "react";
import { Button, Modal, NumberInput } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { commissionBaseLabel } from "@/lib/invoice/place";
import { useMoney } from "@/shared/ui";
import { DrawerWarning } from "./DrawerWarning";
import { TenderReferenceField } from "./TenderReferenceField";
import { hasPendingTenderReference } from "@/modules/pos/lib/tenderReference";
import type {
  CommissionBase, SettlementMethod } from "@/lib/api/types";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";

/**
 * The payment step. Until this existed the till hardcoded an exact-cash
 * settle, which made the settled overlay's CARD label unreachable and its
 * change line permanently zero — half of what a cashier needs from a till is
 * exactly this arithmetic.
 *
 * CARD pins the tendered amount to the total: a card is charged the bill, and
 * an editable field there could only record a number that never happened. CASH
 * takes what the customer handed over and shows the change live, BEFORE
 * confirm — the cashier reads it while the drawer is open, not after.
 *
 * WRITE_OFF (D31) is the third choice and deliberately does not look like the
 * other two. It is not a way to be paid, it is a decision that this bill never
 * will be — so it sits apart, carries the destructive tone, is offered only to
 * a holder of `RESTAURANT_INVOICE_VOID`, and asks a second time before it
 * lands. A walk-out reached by the same tap rhythm as CASH is a walk-out that
 * happens by accident.
 */
export function SettleSheet({
  total,
  breakdown,
  note,
  isSubmitting,
  canWriteOff,
  warning,
  onOpenDrawer,
  warningLinkLabel,
  transferConfirmed = false,
  aggregator = null,
  tableTitle,
  onTakePart,
  onCancel,
  onConfirm,
}: {
  total: number;
  /**
   * Audit round 2 (5.1) — `settleTableTitle`'s answer on a dine-in bill at a
   * table («تحصيل حساب طاولة {table}»); omitted keeps «تحصيل الفاتورة».
   */
  tableTitle?: string;
  /**
   * The stored charge decomposition (D33) of the CLOSED bill being settled,
   * so the sheet can answer "why isn't it the menu sum?" without the cashier
   * cancelling out of the payment flow. Omitted = no lines rendered.
   */
  breakdown?: {
    serviceChargeTotal: number;
    vatTotal: number;
    pricesIncludeTax: boolean;
  };
  /**
   * One plain sentence under the amount due, saying what confirming does to
   * the bill when that is not the usual — on the dark till's OPEN direct
   * takeaway with plates still at the kitchen, that the money is taken and the
   * bill stays open (D126). Omitted = nothing rendered.
   */
  note?: string;
  isSubmitting: boolean;
  /**
   * Whether this member may write a bill off — `RESTAURANT_INVOICE_VOID`,
   * resolved by the page. The server enforces the same rung before it claims
   * the command id; hiding the button is the courtesy, not the gate.
   */
  canWriteOff: boolean;
  /**
   * A standing hint shown above the method toggle — the D25 till hint: with
   * `shiftTrackingEnabled` on and no shift open, the server will refuse this
   * settle as `RESTAURANT_SHIFT_REQUIRED`. Shown here so the cashier learns it
   * BEFORE tapping settle rather than reading it off a toast afterwards.
   * Omitted (not just empty) when there is nothing to warn about, so this
   * component carries no shift-specific logic of its own.
   */
  warning?: string;
  /**
   * FIX THE WARNING WITHOUT LEAVING THIS SHEET (Unit C).
   *
   * Was a LINK to `/manage/shift`, and that was the problem: a cashier with a
   * customer at the counter and a printed cheque on screen had to navigate off the
   * bill, open a drawer in the back office, and find their way back. Nobody does
   * that mid-service. They take the cash and put it in the till, which is the
   * untracked payment D25 exists to prevent — caused by the screen meant to
   * prevent it.
   *
   * So it is a callback now, and the caller opens the drawer dialog OVER this
   * sheet. Omitted for anyone without the drawer rung (WAITER, KITCHEN, a custom
   * role built without it): for them the remedy is a person, not a button, and a
   * button that only ever produces a refusal is the dead instruction this branch
   * exists to remove.
   */
  onOpenDrawer?: () => void;
  /** Label for `onOpenDrawer`. Ignored when there is no callback. */
  warningLinkLabel?: string;
  /**
   * A diner's bank transfer for this bill has been CONFIRMED by staff (D44).
   *
   * Preselects `INSTAPAY`, because at this point the money has already
   * arrived and any other method would be a mis-tap that lands the amount in
   * a drawer it never entered. The cashier can still override — a confirmed
   * transfer that the customer then paid cash for instead is their call, not
   * this sheet's.
   */
  transferConfirmed?: boolean;
  /**
   * F2 — the platform that brought this order, when one did.
   *
   * Non-null ONLY on a bill opened through an aggregator, and that is the whole
   * rule: a dine-in bill or a delivery the shop took on its own phone has no
   * platform to be settled by, and a permanent fourth pill would invite closing
   * a cash bill as "platform" — the same reading D44 refuses for INSTAPAY.
   *
   * When present, `AGGREGATOR` is offered AND PRESELECTED. The platform already
   * has the diner's money; on such a bill every other method is the mis-tap,
   * and the enum member's own doc names the consequence — "a cashier picking
   * CARD to make it go away overstated card takings by the full gross". Until
   * this prop existed the till offered no way to say what had actually
   * happened, so that mis-tap was the only available answer.
   */
  aggregator?: { name: string; commissionPercent: number; commissionBase?: CommissionBase } | null;
  /**
   * B3 — hand off to the split-tender sheet.
   *
   * The entry point is HERE because here is where the cashier discovers they
   * need it: they open the settle sheet for the whole bill, the customer says
   * the card will only take 300, and the alternative has to be one tap away
   * rather than back out on the panel behind the modal.
   *
   * Omitted when there is nothing to hand off to (a bill nobody may settle in
   * pieces), so this sheet carries no rule of its own about when that is.
   *
   * It hands the METHOD over with it. The cashier has already answered "what
   * is this being paid by" on this sheet, and the split sheet used to open on
   * its own `CASH` default regardless — so a card tender typed straight after
   * choosing CARD was recorded as cash, which is a drawer that counts short by
   * the amount and a takings split that names the wrong tender.
   */
  onTakePart?: (method: SettlementMethod) => void;
  onCancel: () => void;
  onConfirm: (
    method: SettlementMethod,
    amount: number,
    /**
     * D163 — what was typed in «مرجع العملية» (raw; may be blank). The caller
     * sends it through `tenderReferenceField`, so it reaches the payload only
     * with CARD/INSTAPAY and only when not blank.
     */
    reference: string,
  ) => void | Promise<void>;
}) {
  const t = useTranslate();
  const money = useMoney();
  /* The platform first: on an aggregator bill the money is already with the
     platform, so CASH is the mis-tap rather than the default. A confirmed
     transfer comes next, for the reason D44 gives. */
  const [method, setMethod] = useState<SettlementMethod>(
    aggregator ? "AGGREGATOR" : transferConfirmed ? "INSTAPAY" : "CASH",
  );
  // A string, not a number: "15" mid-typing must not snap to NaN or 0.
  const [tendered, setTendered] = useState<string>(String(total));
  // The second ask for the write-off. In-sheet rather than a stacked modal:
  // the cashier is mid-decision and must see the bill they are writing off.
  const [writeOffAsked, setWriteOffAsked] = useState(false);
  /* D163 — «مرجع العملية», optional; shown for CARD/INSTAPAY only. */
  const [reference, setReference] = useState("");

  const isWriteOff = method === "WRITE_OFF";
  // Nothing was tendered on a walk-out, so the short-payment floor does not
  // apply to it — the server exempts exactly this method and nothing else.
  /* CARD and INSTAPAY are both charged the bill exactly: there is no tender
     and no change, so an editable amount there could only record a number that
     never happened. Only CASH takes what the customer handed over. */
  const amount = isWriteOff
    ? 0
    : method === "CASH"
      ? Number(tendered)
      : // CARD, INSTAPAY and AGGREGATOR are all charged the bill exactly. On a
        // platform order nothing is handed over at all — the platform collected
        // it days before the food left — so there is no tender to type and no
        // change to give.
        total;
  /*
   * UX-round D20 — THE RULE IS NOT LOOSENED.
   * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
   *
   * `amount >= total` stays exactly as it was. A short tender is still not a
   * settle, and making it one to reach the remedy would record money that
   * never arrived. What changed is what the sheet DOES about it: the remedy
   * offers itself in the footer, beside the refused confirm, instead of as an
   * 11px link two rows below the failure. The remedy is a route, not a
   * relaxation.
   */
  const amountValid = isWriteOff || (Number.isFinite(amount) && amount >= total);
  const change = !isWriteOff && amountValid ? amount - total : 0;
  /* Short because the CASH handed over does not cover the bill — as opposed to
     an empty or half-typed box, which is a person mid-keystroke. Only this
     case has a route to offer. */
  const shortTender =
    !isWriteOff && method === "CASH" && Number.isFinite(amount) && !amountValid;

  // A tender typed over the pre-filled total. Nothing else on this sheet is
  // typed — the method pills and the write-off step are one tap each and
  // re-tappable — so a swipe costs a re-type only here.
  const guard = useDiscardGuard(
    (method === "CASH" && tendered !== String(total)) ||
      hasPendingTenderReference(method, reference),
    onCancel,
  );

  return (
    <>
    <Modal
      isOpen
      onClose={guard.requestClose}
      // One step up the scale (452px -> 576px): the charges, three tender
      // buttons, the amount field and the partial-payment row had outgrown `sm`.
      size="md"
      title={tableTitle ?? t((m) => m.pos.settleTitle)}
      closeLabel={t((m) => m.common.close)}
      data-testid="settle-sheet"
      /* R8 / `ux-form-validation-contract-v1.md` §1 — the reason a confirm is
         refused is on screen BEFORE the press, not discovered by pressing a
         grey rectangle. */
      footerLeft={
        !amountValid && !isWriteOff
          ? t((m) => m.pos.settleShortHint)
          : undefined
      }
      footerActions={
        <>
          <Button variant="outline" size="touch" onClick={guard.requestClose}>
            {t((m) => m.common.cancel)}
          </Button>
          {/* UX-round D20 — THE DEAD END OFFERS ITS OWN REMEDY, IN THE FOOTER.

              A short tender used to leave the cashier with a refused confirm
              and an 11px link two rows above it, inside the scroller. Here the
              route sits beside the button that just refused, at the same
              weight as Cancel, and only while it is the answer. */}
          {onTakePart && shortTender ? (
            <Button
              variant="outline"
              size="touch"
              className="font-extrabold"
              data-testid="settle-take-part-footer"
              onClick={() => onTakePart(method)}
            >
              {t((m) => m.pos.partialOpen)}
            </Button>
          ) : null}
          <Button
            variant={isWriteOff ? "danger" : "primary"}
            size="touch"
            /* UX-round D23 / accessibility R8 — `aria-disabled`, not `disabled`. A
               disabled button is unreachable by a screen reader's tab order
               and silent to a thumb; this one still takes the press and
               answers it, and the answer is already in `footerLeft`. The
               74 other `disabled={` in this app are NOT swept: a control
               refused for a permission the person does not hold stays
               `disabled`, because there is nothing they can do about it. */
            aria-disabled={!amountValid || undefined}
            className={[
              "font-extrabold",
              amountValid ? "" : "opacity-[0.55]",
            ].join(" ")}
            isLoading={isSubmitting}
            data-testid="settle-confirm"
            onClick={() => {
              if (!amountValid) return;
              // Two taps for a walk-out, one for a payment. The first tap
              // only changes what the button SAYS, so the cashier reads the
              // consequence in the words they are about to confirm.
              if (isWriteOff && !writeOffAsked) {
                setWriteOffAsked(true);
                return;
              }
              void onConfirm(method, amount, reference);
            }}
          >
            {isWriteOff
              ? writeOffAsked
                ? t((m) => m.pos.writeOffConfirm)
                : t((m) => m.pos.writeOff)
              : t((m) => m.pos.settleConfirm)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* The bill's decomposition (D33), inside the payment flow. The panel
            foot behind this modal shows the same lines, but on a phone the
            sheet covers it — and "why isn't it 100?" is asked at the moment
            of payment, so the answer must be on the sheet the cashier is
            looking at. Rendered only when a charge exists; an uncharged bill
            keeps the one-line sheet. */}
        {breakdown &&
        (breakdown.serviceChargeTotal > 0 || breakdown.vatTotal > 0) ? (
          <div className="flex flex-col gap-0.5 tabular-nums">
            {breakdown.serviceChargeTotal > 0 ? (
              <div className="flex justify-between text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
                <span>
                  {t((m) =>
                    breakdown.pricesIncludeTax
                      ? m.invoices.serviceChargeIncluded
                      : m.invoices.serviceCharge,
                  )}
                </span>
                <span>{money(breakdown.serviceChargeTotal)}</span>
              </div>
            ) : null}
            {breakdown.vatTotal > 0 ? (
              <div className="flex justify-between text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
                <span>
                  {t((m) =>
                    breakdown.pricesIncludeTax
                      ? m.invoices.vatIncluded
                      : m.invoices.vat,
                  )}
                </span>
                <span>{money(breakdown.vatTotal)}</span>
              </div>
            ) : null}
          </div>
        ) : null}
        {/* THE AMOUNT DUE IS THE LARGEST THING ON THE SHEET (P5, obligation
            1). It was `text-lg` — the same size as the tendered box below it —
            on the one surface where a cashier reads a figure aloud to a guest
            who is listening. */}
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-semibold text-[color:var(--text-muted)]">
            {t((m) => m.pos.running)}
          </span>
          <span
            className="text-[length:calc(var(--font-size-3xl)_+_6px)] font-extrabold leading-none tabular-nums"
            data-testid="settle-total"
          >
            {money(total)}
          </span>
        </div>

        {note ? (
          <p
            className="text-xs font-semibold text-[color:var(--text-secondary)]"
            data-testid="settle-note"
          >
            {note}
          </p>
        ) : null}

        {warning ? (
          <DrawerWarning
            message={warning}
            actionLabel={warningLinkLabel ?? ""}
            onOpenDrawer={warningLinkLabel ? onOpenDrawer : undefined}
            testId="settle-shift-warning"
          />
        ) : null}

        <div className="flex gap-2">
          {/* INSTAPAY only when a transfer for this bill has actually been
              confirmed (D44). A permanent third pill would invite closing an
              unpaid bill as "transfer" on nothing but the customer's word. */}
          {((aggregator
            ? transferConfirmed
              ? (["AGGREGATOR", "CASH", "CARD", "INSTAPAY"] as const)
              : (["AGGREGATOR", "CASH", "CARD"] as const)
            : transferConfirmed
              ? (["CASH", "CARD", "INSTAPAY"] as const)
              : (["CASH", "CARD"] as const)) as readonly SettlementMethod[]).map(
            (option) => (
            <Button
              key={option}
              variant={method === option ? "primary" : "outline"}
              size="touch"
              className="flex-1 font-extrabold"
              aria-pressed={method === option}
              data-testid={`settle-method-${option}`}
              onClick={() => {
                setWriteOffAsked(false);
                setMethod(option);
              }}
            >
              {option === "CASH"
                ? t((m) => m.pos.cash)
                : option === "CARD"
                  ? t((m) => m.pos.card)
                  : option === "AGGREGATOR"
                    ? // The PLATFORM'S OWN NAME, not the word "platform": a
                      // cashier confirming a Talabat order should read
                      // "Talabat" on the pill they are about to tap.
                      aggregator?.name ?? t((m) => m.pos.aggregatorMethod)
                    : t((m) => m.pos.instapay)}
            </Button>
            ),
          )}
        </div>

        {/* WHAT PICKING THE PLATFORM DOES, said once and in numbers.

            "Settled by Talabat" is not a payment method a cashier has met
            before, and the thing they need to know is not that it exists but
            that the money is NOT in the drawer they will count tonight and
            that the rate comes off it. Without this the pill reads as a fourth
            way to be paid, which is exactly how the gross ends up in a drawer
            it never entered. */}
        {method === "AGGREGATOR" && aggregator ? (
          <p
            className="rounded-[var(--radius-list)] bg-[color:var(--surface-alt)] px-3 py-2 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold leading-relaxed text-[color:var(--text-secondary)]"
            data-testid="settle-aggregator-hint"
          >
            {t((m) => m.pos.aggregatorSettleHint, {
              name: aggregator.name,
              rate: String(aggregator.commissionPercent),
            })}{" "}
            {/* T15 — what the rate is taken on, off the bill's snapshot. */}
            {t((m) => m.pos.aggregatorSettleHintBase, {
              base: commissionBaseLabel(aggregator.commissionBase ?? "TOTAL", t),
            })}
          </p>
        ) : null}

        {/* D163 — the optional reference, under the pills, only while CARD
            or INSTAPAY is chosen. It never refuses the confirm. */}
        <TenderReferenceField
          method={method}
          value={reference}
          onChange={setReference}
          disabled={isSubmitting}
          testId="settle-reference"
        />

        {/* On its own row, in the destructive secondary tone: it is not a
            third way to be paid, and a cashier scanning three equal pills
            would eventually tap it by rhythm. */}
        {canWriteOff ? (
          <Button
            variant={isWriteOff ? "danger" : "danger-outline"}
            size="touch"
            fullWidth
            className="font-extrabold"
            aria-pressed={isWriteOff}
            data-testid="settle-method-WRITE_OFF"
            onClick={() => {
              setMethod("WRITE_OFF");
              setWriteOffAsked(false);
            }}
          >
            {t((m) => m.pos.writeOff)}
          </Button>
        ) : null}

        {/* The confirm STEP, not a confirm dialog: it replaces the settle
            button below with a two-tap sequence, so the bill stays on screen
            while the sentence explaining the consequence is read. */}
        {isWriteOff ? (
          <div
            className="flex flex-col gap-1 rounded-[var(--radius-list)] bg-[color:var(--danger-100,var(--surface-alt))] px-3 py-2.5"
            data-testid="settle-write-off-notice"
          >
            <span className="text-sm font-extrabold text-[color:var(--danger)]">
              {t((m) => m.pos.writeOffTitle)}
            </span>
            <span className="text-xs font-semibold text-[color:var(--text-secondary)]">
              {t((m) => m.pos.writeOffBody)}
            </span>
          </div>
        ) : null}

        {method === "CASH" ? (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-bold text-[color:var(--text-muted)]">
              {t((m) => m.pos.tenderedLabel)}
            </span>
            <NumberInput
              name="tendered"
              allowDecimal
              min={0}
              value={tendered}
              onChange={(event) => setTendered(event.target.value)}
              hasError={!amountValid}
              data-testid="settle-tendered"
              className="text-lg font-extrabold tabular-nums"
            />
            {/* THE SENTENCE MOVED, IT DID NOT GO.

                `pos.settleShortHint` used to print here, under the field, and
                also had to appear beside the confirm once that button became
                `aria-disabled` — two copies of one sentence in two weights on a
                390px money sheet. It now lives in the footer, next to the
                button it explains and OUTSIDE the scroller, so it is on screen
                whenever the refused confirm is. The field keeps `hasError`,
                which is the field-level signal; the words belong with the
                control they are about. */}
          </label>
        ) : null}

        {amountValid && change > 0 ? (
          <div
            className="flex items-baseline justify-between rounded-[var(--radius-list)] bg-[color:var(--warning-100,var(--surface-alt))] px-3 py-2"
            data-testid="settle-change"
          >
            <span className="text-sm font-bold">
              {t((m) => m.pos.changeDue)}
            </span>
            <span className="text-lg font-extrabold tabular-nums text-[color:var(--warning)]">
              {money(change)}
            </span>
          </div>
        ) : null}

        {/* B3 — the other way to be paid, offered where the cashier finds out
            they need it. A text control and not a third pill: it is not a
            METHOD, it is a different shape of payment, and a cashier scanning
            equal-weight buttons would tap it by rhythm. Hidden on a write-off,
            which is a decision about the whole bill and cannot be partial. */}
        {/* The quiet door, for a cashier who knows before typing that this
            bill is going to be split. It steps aside the moment the tender
            goes short: the route is then in the footer, beside the confirm
            that refused, and two controls for one act is one too many. */}
        {onTakePart && !isWriteOff && !shortTender ? (
          <button
            type="button"
            data-testid="settle-take-part"
            onClick={() => onTakePart(method)}
            className="self-start text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--info)] transition-colors hover:text-[color:var(--text)]"
          >
            {t((m) => m.pos.partialOpen)}
          </button>
        ) : null}

      </div>
    </Modal>
    <UnsavedChangesPrompt
      isOpen={guard.asking}
      onDiscard={guard.discard}
      onKeepEditing={guard.keepEditing}
      testId="settle-discard-prompt"
    />
    </>
  );
}
